import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TrendingUp, BarChart3, HelpCircle } from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend
} from 'recharts';
import { format, subMonths, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface MonthlyData {
  month: string;
  monthLabel: string;
  homeCases: number;
  pcpCases: number;
  urgentCareCases: number;
  erCases: number;
  totalSavings: number;
  homeSavings: number;
  pcpSavings: number;
  urgentCareSavings: number;
}

interface CostDefaults {
  HOME: number;
  PCP: number;
  URGENT_CARE: number;
  ER_NOW: number;
}

type RecommendationType = 'HOME' | 'PCP' | 'URGENT_CARE' | 'ER_NOW';

interface CostSavingsTrendChartProps {
  agencyId: string | null;
  customHospitalCost?: number | null;
  selectedRecommendations?: RecommendationType[];
  patientIds?: string[];
}

export default function CostSavingsTrendChart({ 
  agencyId, 
  customHospitalCost, 
  selectedRecommendations = ['HOME', 'PCP', 'URGENT_CARE', 'ER_NOW'],
  patientIds
}: CostSavingsTrendChartProps) {
  const chartColors = useChartColors();
  const [monthlyData, setMonthlyData] = useState<MonthlyData[]>([]);
  const [costDefaults, setCostDefaults] = useState<CostDefaults>({
    HOME: 150,
    PCP: 450,
    URGENT_CARE: 500,
    ER_NOW: 2500
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCostDefaults();
  }, []);

  useEffect(() => {
    loadTrendData();
  }, [agencyId, costDefaults, customHospitalCost, selectedRecommendations, patientIds]);

  const loadCostDefaults = async () => {
    try {
      const { data, error } = await supabase
        .from('hospital_visit_cost_defaults')
        .select('recommendation_level, default_cost');

      if (error) throw error;

      if (data) {
        const defaults: CostDefaults = {
          HOME: 150,
          PCP: 450,
          URGENT_CARE: 500,
          ER_NOW: 2500
        };
        data.forEach((row: { recommendation_level: string; default_cost: number }) => {
          if (row.recommendation_level in defaults) {
            defaults[row.recommendation_level as keyof CostDefaults] = row.default_cost;
          }
        });
        setCostDefaults(defaults);
      }
    } catch (error) {
      console.error('Error loading cost defaults:', error);
    }
  };

  const loadTrendData = async () => {
    try {
      // Use the optimized DB function for trend data
      // Pass null for _agency_id to aggregate all agencies
      const { data: trendResult, error } = await supabase.rpc('get_cost_savings_trend', {
        _agency_id: agencyId,
        _months: 12
      });

      if (error) throw error;

      const effectiveErCost = customHospitalCost || costDefaults.ER_NOW;
      
      // Initialize last 12 months
      const monthlyMap = new Map<string, MonthlyData>();
      for (let i = 11; i >= 0; i--) {
        const monthDate = subMonths(new Date(), i);
        const monthKey = format(monthDate, 'yyyy-MM');
        monthlyMap.set(monthKey, {
          month: monthKey,
          monthLabel: format(monthDate, 'MMM yyyy'),
          homeCases: 0,
          pcpCases: 0,
          urgentCareCases: 0,
          erCases: 0,
          totalSavings: 0,
          homeSavings: 0,
          pcpSavings: 0,
          urgentCareSavings: 0
        });
      }

      // Process trend data from DB function
      if (trendResult && Array.isArray(trendResult)) {
        trendResult.forEach((row: any) => {
          if (!row.month_start) return;
          
          const monthKey = format(new Date(row.month_start), 'yyyy-MM');
          const monthData = monthlyMap.get(monthKey);
          
          if (monthData) {
            // Apply recommendation filter
            monthData.homeCases = selectedRecommendations.includes('HOME') ? (row.home_cases || 0) : 0;
            monthData.pcpCases = selectedRecommendations.includes('PCP') ? (row.pcp_cases || 0) : 0;
            monthData.urgentCareCases = selectedRecommendations.includes('URGENT_CARE') ? (row.urgent_care_cases || 0) : 0;
            monthData.erCases = selectedRecommendations.includes('ER_NOW') ? (row.er_cases || 0) : 0;
            
            // Calculate savings only for selected recommendations
            monthData.homeSavings = monthData.homeCases * (effectiveErCost - costDefaults.HOME);
            monthData.pcpSavings = monthData.pcpCases * (effectiveErCost - costDefaults.PCP);
            monthData.urgentCareSavings = monthData.urgentCareCases * (effectiveErCost - costDefaults.URGENT_CARE);
            monthData.totalSavings = monthData.homeSavings + monthData.pcpSavings + monthData.urgentCareSavings;
          }
        });
      }

      setMonthlyData(Array.from(monthlyMap.values()));
    } catch (error) {
      console.error('Error loading trend data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div 
          className="rounded-lg shadow-lg p-3 border bg-white"
          style={chartColors.tooltipStyle}
        >
          <p className="font-medium mb-2 text-gray-900">{label}</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600">{entry.name}:</span>
              <span className="font-medium text-gray-900">
                {entry.name.includes('Savings') ? formatCurrency(entry.value) : entry.value}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Calculate totals
  const totalSavings = monthlyData.reduce((sum, m) => sum + m.totalSavings, 0);
  const avgMonthlySavings = monthlyData.length > 0 ? totalSavings / monthlyData.length : 0;
  const latestMonth = monthlyData[monthlyData.length - 1];
  const previousMonth = monthlyData[monthlyData.length - 2];
  const monthOverMonthChange = latestMonth && previousMonth && previousMonth.totalSavings > 0
    ? ((latestMonth.totalSavings - previousMonth.totalSavings) / previousMonth.totalSavings) * 100
    : 0;

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>Savings Trend</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse h-64 bg-muted rounded"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              <span>Cost Savings Trend</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p className="text-sm">Always shows the last 12 months of data. Not affected by the date filter above.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
            <CardDescription>
              Monthly savings breakdown over the last 12 months
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">12-Month Total</p>
              <p className="text-xl font-bold text-green-600">{formatCurrency(totalSavings)}</p>
            </div>
            {monthOverMonthChange !== 0 && (
              <Badge 
                variant="secondary" 
                className={monthOverMonthChange > 0 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
                }
              >
                {monthOverMonthChange > 0 ? '+' : ''}{monthOverMonthChange.toFixed(1)}% MoM
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="savings" className="space-y-4">
          <TabsList>
            <TabsTrigger value="savings">Total Savings</TabsTrigger>
            <TabsTrigger value="breakdown">Savings Breakdown</TabsTrigger>
            <TabsTrigger value="cases">Cases by Type</TabsTrigger>
          </TabsList>

          <TabsContent value="savings" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.4}/>
                    <stop offset="50%" stopColor="#34D399" stopOpacity={0.2}/>
                    <stop offset="100%" stopColor="#6EE7B7" stopOpacity={0.05}/>
                  </linearGradient>
                  <filter id="glow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="totalSavings" 
                  name="Total Savings"
                  stroke="#10B981" 
                  strokeWidth={3}
                  fillOpacity={1} 
                  fill="url(#colorSavings)"
                  dot={{ fill: '#10B981', strokeWidth: 2, stroke: '#fff', r: 4 }}
                  activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: '#10B981', filter: 'url(#glow)' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </AreaChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="breakdown" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="homeSavings" name="Home Care Savings" stackId="a" fill="#8B5CF6" radius={[0, 0, 0, 0]} animationDuration={1200} animationEasing="ease-out" />
                <Bar dataKey="pcpSavings" name="PCP Savings" stackId="a" fill="#10B981" animationDuration={1200} animationEasing="ease-out" animationBegin={200} />
                <Bar dataKey="urgentCareSavings" name="Urgent Care Savings" stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} animationDuration={1200} animationEasing="ease-out" animationBegin={400} />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>

          <TabsContent value="cases" className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <RechartsTooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="homeCases" name="Home Care" stackId="a" fill="#8B5CF6" radius={[0, 0, 0, 0]} animationDuration={1000} animationEasing="ease-out" />
                <Bar dataKey="pcpCases" name="PCP Visits" stackId="a" fill="#10B981" animationDuration={1000} animationEasing="ease-out" animationBegin={150} />
                <Bar dataKey="urgentCareCases" name="Urgent Care" stackId="a" fill="#F59E0B" animationDuration={1000} animationEasing="ease-out" animationBegin={300} />
                <Bar dataKey="erCases" name="ER Required" stackId="a" fill="#EF4444" radius={[4, 4, 0, 0]} animationDuration={1000} animationEasing="ease-out" animationBegin={450} />
              </BarChart>
            </ResponsiveContainer>
          </TabsContent>
        </Tabs>

        {/* Monthly Summary */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-4 border-t border-border">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Avg. Monthly Savings</p>
            <p className="text-lg font-bold text-foreground">{formatCurrency(avgMonthlySavings)}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Best Month</p>
            <p className="text-lg font-bold text-green-600">
              {formatCurrency(Math.max(...monthlyData.map(m => m.totalSavings)))}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Total Cases Diverted</p>
            <p className="text-lg font-bold text-foreground">
              {monthlyData.reduce((sum, m) => sum + m.homeCases + m.pcpCases + m.urgentCareCases, 0)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}